// Parent-gate modal: password re-prompt before opening Settings /
// Elternbereich / logout / objective-delete. The credential is verified
// server-side at /auth/v3/verify-parent-gate; on success the backend issues
// a 5-minute elevation token that subsequent sensitive endpoints require.
//
// The previous version had a client-side math fallback that called onPass()
// without backend verification — removed in 2026-04-25 because solving the
// math problem locally bypassed server-side enforcement on read-only gated
// surfaces (parent dashboard). All verification is now server-side.
//
// Accepted credential: the parent's account password only. The child PIN is
// not accepted — the child knows it, so it cannot guard parent-only actions.

import { useEffect, useRef, useState } from 'react';
import { verifyParentGateV3 } from '../../api/client';

type Phase = 'credential' | 'checking' | 'locked';

const MAX_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 30;

interface Props {
  purpose: string;          // e.g. "open_settings", "open_parent_dashboard", "logout"
  klasse: number;
  onPass: () => void;
  onCancel: () => void;
}

const FOCUSABLE = 'button:not([disabled]), [href], textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ParentGate({ purpose, klasse: _klasse, onPass, onCancel }: Props) {
  const [phase, setPhase] = useState<Phase>('credential');
  const [credential, setCredential] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Tick during the lockout so the countdown UI updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (phase !== 'locked') return;
    const id = window.setInterval(() => setTick((t) => t + 1), 500);
    return () => window.clearInterval(id);
  }, [phase]);
  useEffect(() => {
    if (phase === 'locked' && lockoutUntil && Date.now() >= lockoutUntil) {
      setPhase('credential');
      setLockoutUntil(null);
      setAttempts(0);
      setError(null);
    }
  });

  useEffect(() => {
    const id = window.setTimeout(() => firstFieldRef.current?.focus(), 0);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
    window.addEventListener('keydown', onKey);
    return () => { window.clearTimeout(id); window.removeEventListener('keydown', onKey); };
  }, [onCancel]);

  async function submitCredential() {
    if (!credential) { setError('Bitte das Passwort deines Eltern-Kontos eingeben.'); return; }
    setPhase('checking');
    setError(null);
    try {
      const { ok } = await verifyParentGateV3(credential, purpose);
      if (ok) { onPass(); return; }
      const next = attempts + 1;
      setAttempts(next);
      setCredential('');
      if (next >= MAX_ATTEMPTS) {
        setPhase('locked');
        setLockoutUntil(Date.now() + LOCKOUT_SECONDS * 1000);
        setError(`Zu viele Versuche. Bitte ${LOCKOUT_SECONDS} Sekunden warten.`);
      } else {
        setPhase('credential');
        setError(`Das stimmt nicht (${next}/${MAX_ATTEMPTS}). Bitte erneut versuchen.`);
      }
    } catch {
      setPhase('credential');
      setError('Verbindung fehlgeschlagen. Bitte nochmal versuchen.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="parent-gate-title"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(30,24,18,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, animation: 'fade-in-up 0.2s ease-out',
      }}
    >
      <div
        ref={dialogRef}
        style={{
          background: 'var(--parchment)', padding: 28, borderRadius: 6,
          width: 'min(420px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          fontFamily: 'var(--serif)',
        }}
      >
        <div id="parent-gate-title" style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 600, marginBottom: 6 }}>
          Nur für Eltern
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 18 }}>
          Bitte das Passwort deines Eltern-Kontos eingeben, um diesen Bereich zu öffnen.
        </div>

        <label htmlFor="parent-gate-cred" style={{
          fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'var(--ink-muted)',
        }}>Passwort (Eltern-Konto)</label>
        <input
          id="parent-gate-cred"
          ref={firstFieldRef}
          type="password"
          autoComplete="current-password"
          value={credential}
          disabled={phase !== 'credential'}
          onChange={e => setCredential(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && phase === 'credential') submitCredential(); }}
          style={{
            width: '100%', marginTop: 4, padding: '12px 14px',
            fontFamily: 'var(--body)', fontSize: 16,
            boxSizing: 'border-box',
            border: '1px solid rgba(60,50,40,0.25)', borderRadius: 3,
          }}
        />

        {error && (
          <div role="alert" style={{ marginTop: 12, color: 'var(--wrong)', fontSize: 13, lineHeight: 1.4 }}>
            {error}
            {phase === 'locked' && lockoutUntil && (
              <span> ({Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))}s)</span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              minHeight: 44, padding: '10px 18px', background: 'transparent',
              border: '1px solid rgba(60,50,40,0.18)', borderRadius: 3,
              fontFamily: 'var(--sans)', fontSize: 13, cursor: 'pointer',
            }}
          >Abbrechen</button>
          <button
            onClick={submitCredential}
            disabled={phase !== 'credential'}
            style={{
              minHeight: 44, padding: '10px 20px', background: 'var(--amber)',
              color: '#fff', border: 'none', borderRadius: 3,
              fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 700,
              letterSpacing: '0.04em',
              cursor: phase === 'credential' ? 'pointer' : 'default',
              opacity: phase === 'credential' ? 1 : 0.6,
            }}
          >{phase === 'checking' ? 'Prüfe…' : phase === 'locked' ? 'Gesperrt' : 'Bestätigen'}</button>
        </div>
      </div>
    </div>
  );
}
